import { apiFetch } from "@/api/api-fetch";

export interface TestContentExample {
  title: string;
  content: string;
}

export interface VoiceProfile {
  version: string;
  updatedAt: string;
  status: string;
  usageCount: number;
  editingReduction: number;
  consistencyScore: number;
  tone: string;
  sentenceStructure: string;
  vocabulary: string;
  perspective: string;
  emphasis: string;
  avoidList: string[];
  changesSummary?: string;
  beforeAfterExample?: BeforeAfterExample | null;
  // Optional persisted
  testContentExample?: TestContentExample;
}

export interface VersionHistoryItem {
  version: string;
  date: string;
  changes: string;
  usedInFixes: number;
  status: string;
}

export interface GeneratedAttributes {
  tone: { detected: string[]; confidence: number };
  readabilityLevel: { grade: string; fleschScore: number; confidence: number };
  commonPhrases: string[];
  sentenceLength: { avg: number; range: string; confidence: number };
}

export interface BeforeAfterExample {
  beforeText: string;
  afterText: string;
  analysis?: { changes: string[]; whyBetter: string[] };
}

function getActiveBrand(): string {
  return (localStorage.getItem("bm_brand") || "default").trim() || "default";
}

export async function getCurrentProfile(): Promise<VoiceProfile> {
  const brand = getActiveBrand();
  const res = await apiFetch<any>(`/brand-voice/current?brand=${encodeURIComponent(brand)}`);
  return res.currentProfile as VoiceProfile;
}

export async function getGeneratedAttributes(): Promise<GeneratedAttributes> {
  const brand = getActiveBrand();
  const res = await apiFetch<any>(`/brand-voice/current?brand=${encodeURIComponent(brand)}`);
  return res.generatedAttributes as GeneratedAttributes;
}

export async function getVersionHistory(): Promise<VersionHistoryItem[]> {
  const brand = getActiveBrand();
  const res = await apiFetch<any>(`/brand-voice/history?brand=${encodeURIComponent(brand)}`);
  return (res.items || []) as VersionHistoryItem[];
}

export async function getBrandVoiceVersion(version: string): Promise<{ profile: VoiceProfile; generatedAttributes: GeneratedAttributes }> {
  const brand = getActiveBrand();
  const res = await apiFetch<any>(
    `/brand-voice/version?brand=${encodeURIComponent(brand)}&version=${encodeURIComponent(version)}`
  );
  return {
    profile: res.profile as VoiceProfile,
    generatedAttributes: res.generatedAttributes as GeneratedAttributes,
  };
}

export async function generateBrandVoice(params: {
  websiteUrl: string;
  sampleText?: string;
  file?: File | null;
}): Promise<{ draftProfile: VoiceProfile; generatedAttributes: GeneratedAttributes; beforeAfterExample: BeforeAfterExample }> {
  const brand = getActiveBrand();

  const fd = new FormData();
  fd.append("brand", brand);
  fd.append("websiteUrl", params.websiteUrl || "");
  fd.append("sampleText", params.sampleText || "");
  if (params.file) fd.append("file", params.file);

  const res = await apiFetch<any>("/brand-voice/generate", { method: "POST", body: fd });

  return {
    draftProfile: res.draftProfile as VoiceProfile,
    generatedAttributes: res.generatedAttributes as GeneratedAttributes,
    beforeAfterExample: res.beforeAfterExample as BeforeAfterExample,
  };
}

export async function saveBrandVoice(params: {
  profile: VoiceProfile;
  generatedAttributes: GeneratedAttributes;
  changesSummary?: string;
}): Promise<{ ok: boolean; version: number }> {
  const brand = getActiveBrand();

  const fd = new FormData();
  fd.append("brand", brand);
  fd.append("profileJson", JSON.stringify(params.profile || {}));
  fd.append("generatedAttributesJson", JSON.stringify(params.generatedAttributes || {}));
  fd.append("changesSummary", params.changesSummary || "Profile updated");

  return await apiFetch<{ ok: boolean; version: number }>("/brand-voice/save", { method: "POST", body: fd });
}

export async function setCurrentBrandVoiceVersion(version: string): Promise<{ ok: boolean }> {
  const brand = getActiveBrand();
  const fd = new FormData();
  fd.append("brand", brand);
  fd.append("version", String(parseInt(version, 10)));
  return await apiFetch<{ ok: boolean }>("/brand-voice/set-current", { method: "POST", body: fd });
}

export async function generateTestContent(params: {
  profile: VoiceProfile;
  generatedAttributes: GeneratedAttributes;
  scenario?: string;
  focusAttribute?: string; // "all" | specific attribute name
}): Promise<{ title: string; content: string }> {
  const brand = getActiveBrand();
  const fd = new FormData();
  fd.append("brand", brand);
  fd.append("profileJson", JSON.stringify(params.profile || {}));
  fd.append("generatedAttributesJson", JSON.stringify(params.generatedAttributes || {}));
  fd.append("scenario", params.scenario || "Write a short product update announcement");
  fd.append("focusAttribute", params.focusAttribute || "all");

  return await apiFetch<{ title: string; content: string }>("/brand-voice/test-content", {
    method: "POST",
    body: fd,
  });
}